import type { JobDetailsDisplayedEvent } from '@seek/data-tracking-event-catalogue';
import { useHubble } from '@seek/hubble';
import {
  type ActivityType,
  addTags,
  type PageName,
  type SolMetadata,
} from '@seek/sol-js';
import { useEffect, useMemo, useState } from 'react';

import { useAppConfig } from 'src/components/App/useAppConfig';
import type {
  GetJobDetailsQuery,
  JobDetailsWorkArrangements,
} from 'src/graphql/graphql';

type ArrangementsType = NonNullable<
  JobDetailsDisplayedEvent['data']['workArrangements']
>[number];

const isValidJobMode = (workArrangement?: string | null) => {
  const validJobModes = ['HYBRID', 'REMOTE', 'ONSITE'];
  return validJobModes.includes(workArrangement ?? '');
};

export const mapJobArrangementsType = (
  workArrangements: JobDetailsWorkArrangements['arrangements'],
): JobDetailsDisplayedEvent['data']['workArrangements'] =>
  workArrangements
    ?.filter((wa) => isValidJobMode(wa?.type))
    .map((wa) => wa?.type as ArrangementsType);

const useHubbleTrackActivity = ({
  trackType,
  solMetadata,
  page,
  topApplicantBadgeShown,
  linkout,
  isSalaryMatch,
  isAddSalaryPrefNudgeShown,
  workArrangements,
}: {
  trackType: ActivityType;
  solMetadata: SolMetadata;
  page: PageName;
  topApplicantBadgeShown: boolean;
  linkout: boolean;
  isSalaryMatch: boolean;
  isAddSalaryPrefNudgeShown: boolean;
  workArrangements?: NonNullable<
    NonNullable<GetJobDetailsQuery>['jobDetails']
  >['workArrangements'];
}) => {
  const { environment } = useAppConfig();
  const [tracked, setTracked] = useState(false);
  const hubble = useHubble();

  const enhancedSolMetaData = useMemo(
    () =>
      addTags(solMetadata, {
        // TODO: we will need to revisit below tag if we decide to auto select the first job
        // Expected values for this tag is summarised in this page:
        // https://myseek.atlassian.net/wiki/spaces/AJDT/pages/2433746322/Job+Details+Viewed+Hubble+Tracking
        'sol:eventTrigger': 'default',
        // End of tag to revisit
      }),
    [solMetadata],
  );

  useEffect(() => {
    if (enhancedSolMetaData && !tracked) {
      setTracked(true);

      hubble.trackEvent('job_details_displayed', {
        applicationTipsType: 'NotShown',
        jobId: enhancedSolMetaData.jobId,
        topApplicantBadgeShown,
        solMetadata: enhancedSolMetaData,
        linkout,
        currentPage: page,
        isSalaryMatch,
        isAddSalaryPrefNudgeShown,
        workArrangements: mapJobArrangementsType(
          workArrangements?.arrangements,
        ),
      });
    }
  }, [
    enhancedSolMetaData,
    environment,
    trackType,
    page,
    setTracked,
    tracked,
    hubble,
    topApplicantBadgeShown,
    linkout,
    isSalaryMatch,
    isAddSalaryPrefNudgeShown,
    workArrangements?.arrangements,
  ]);
};

export default useHubbleTrackActivity;
